import { css } from '@emotion/react'
import {
  DesignToken,
  RadiusToken,
  Modal,
} from '@livechat/design-system-react-components'

import ChevronRight from '@/assets/icons/chevron-right.inline.svg'
import ChevronLeft from '@/assets/icons/chevron-left.inline.svg'
import { VideoPlayer } from '@/components/VideoPlayer'
import mediaQueries from '@/layout/media-queries'
import useSwipe, { DIRECTIONS } from '@/hooks/useSwipe'
import { LocalImage } from '@/components/LocalImage'

import type { MediaSlide } from './interfaces'

const iconsHoverCss = css`
  opacity: 1;
  transition: opacity 0.3 ease-in;

  &:hover {
    opacity: 0.7;
  }
`

const modalCss = css`
  width: 85%;
  min-height: 200px;
  max-width: 1000px;
  min-width: 320px;
  background: none;
  box-shadow: none;

  button {
    color: var(${DesignToken.ColorWhite});
    top: -14px;
    right: 6px;
    box-shadow: none;

    &:hover {
      color: var(${DesignToken.ColorWhite});
    }

    svg {
      transform: scale(1.2);
    }
  }
`

const previewBoxCss = css`
  position: relative;
  width: 100%;

  &::after {
    content: '';
    display: block;
    padding-bottom: 56.25%;
  }
`

const previewBoxContentCss = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  /* Just for decoration */
  display: flex;
  align-items: center;
  justify-content: center;
`

const previewBoxMediaCss = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  animation: fadein 100ms ease-in;

  &,
  iframe {
    border-radius: var(${RadiusToken.Radius1});
  }

  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const chevronCss = css`
  cursor: pointer;
  color: var(${DesignToken.ColorWhite});
  position: absolute;
  display: none;
  top: 45%;
  ${iconsHoverCss};

  ${mediaQueries.smallScreen()} {
    display: block;
  }
`

const leftChevronCss = css`
  ${chevronCss}
  left: -56px;
  top: 45%;
`

const rightChevronCss = css`
  ${chevronCss}
  right: -56px;
  top: 45%;
`

const getPreviewItem = (item: MediaSlide) => {
  if (!item) {
    return null
  }

  if (item.type === 'video') {
    return (
      <div css={previewBoxCss}>
        <div css={previewBoxContentCss}>
          <VideoPlayer
            width="100%"
            height="100%"
            controls
            css={previewBoxMediaCss}
            url={item.url}
          />
        </div>
      </div>
    )
  }

  return (
    <div css={previewBoxCss}>
      <div css={previewBoxContentCss}>
        <LocalImage
          image={item.image}
          fallbackUrl={item.original || item.small || item.large}
          alt={item.title || ''}
          css={previewBoxMediaCss}
        />
      </div>
    </div>
  )
}

interface Props {
  handleClose: () => void
  nextImg: () => void
  prevImg: () => void
  mediaItem: MediaSlide
  mediaList?: MediaSlide[]
}

export const MediaPreview = ({
  handleClose,
  nextImg,
  prevImg,
  mediaItem,
  mediaList,
}: Props) => {
  const handleSwipe = (direction: string) => {
    switch (direction) {
      case DIRECTIONS.LEFT:
        nextImg()
        break

      case DIRECTIONS.RIGHT:
        prevImg()
        break

      default:
    }
  }

  useSwipe({
    touchSurface: document,
    callback: handleSwipe,
  })

  const shouldDisplayChevrons = mediaList && mediaList.length > 1

  return (
    <Modal fullSpaceContent onClose={handleClose} css={modalCss} labelHeading>
      {shouldDisplayChevrons && (
        <ChevronLeft css={leftChevronCss} onClick={prevImg} />
      )}

      {getPreviewItem(mediaItem)}

      {shouldDisplayChevrons && (
        <ChevronRight css={rightChevronCss} onClick={nextImg} />
      )}
    </Modal>
  )
}
